import { displayResultMessage } from "utils/misc";
import { sharedLib } from "@shared/services/shared.service.ts";
import {
  CreatePayoutMethodRequest,
  CryptoAsset,
  CryptoNetwork,
  PaymentMethodType,
  PayoutMethod,
  PayoutMethodSource,
  LanguageNameSpaces,
} from "@shared/types";
import { useState } from "react";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";

interface UseCreatePayoutMethodProps {
  closeModal: () => void;
  source: PayoutMethodSource;
  isEdit?: boolean;
  payoutMethod?: PayoutMethod;
}

const useCreatePayoutMethod = ({
  closeModal,
  source,
  isEdit,
  payoutMethod,
}: UseCreatePayoutMethodProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.AFFILIATION);
  const [asset, setAsset] = useState<CryptoAsset>();
  const [network, setNetwork] = useState<CryptoNetwork>();

  const [createPayoutMethod, { isLoading: createLoading }] =
    sharedLib.sharedApi.settings.useCreatePayoutMethodMutation();

  const [editPayoutMethod, { isLoading: editLoading }] =
    sharedLib.sharedApi.settings.useEditPayoutMethodMutation();

  const getFunction = (values: CreatePayoutMethodRequest) => {
    if (isEdit && payoutMethod) {
      return editPayoutMethod({
        name: values.name,
        payout_method_id: payoutMethod.payout_method_id,
      });
    } else {
      const args = {
        source,
        name: values.name,
        type: values.type,
      };
      if (values.type === PaymentMethodType.PAYPAL) {
        return createPayoutMethod({
          ...args,
          email: values.email,
        });
      } else if (values.type === PaymentMethodType.CRYPTO) {
        return createPayoutMethod({
          ...args,
          asset,
          network,
          address: values.address,
          tag: values.tag,
        });
      }
    }
  };

  const createPayoutMethodHandler = (values: CreatePayoutMethodRequest) => {
    displayResultMessage(
      getFunction(values)!.unwrap(),
      isEdit
        ? t("messages.payout_method_updated")
        : t("messages.payout_method_created"),
      isEdit
        ? t("messages.payout_method_update_failed")
        : t("messages.payout_method_create_failed")
    )
      .then(() => closeModal())
      .catch(() => {});
  };

  return {
    createLoading,
    editLoading,
    createPayoutMethodHandler,
    asset,
    setAsset,
    network,
    setNetwork,
  };
};

export default useCreatePayoutMethod;
